import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { pulseFetch } from '../../pulse/pulseClient';
import { useAuth } from '../../auth/AuthProvider';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { alertsFixture, eventsFixture, decisionsFixture } from './fixture';

/**
 * Pulse-Security-Dashboard. Pulse akzeptiert denselben CORE-Bearer wie
 * Orbit selbst — Operator hat i.d.R. `tenantId: null` (nicht an einen
 * Kunden-Tenant gebunden), Pulse liefert dann alle Alerts/Events statt
 * pro-Tenant zu filtern (siehe alerts.service.js Kommentar).
 *
 * Endpoints: GET /v1/alerts, POST /v1/alerts/:id/ack, GET /v1/security/events,
 * GET /v1/decisions
 */
function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function usePulseAlerts({ unacknowledgedOnly = false, limit = 50 } = {}) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['pulse-alerts', { unacknowledgedOnly, limit }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return alertsFixture(); }
      const qs = new URLSearchParams({ limit: String(limit) });
      if (unacknowledgedOnly) qs.set('unacknowledged_only', '1');
      return pulseFetch(`/v1/alerts?${qs.toString()}`, { headers: authHeader(auth.getAccessToken()) });
    },
    refetchInterval: 20_000,
  });
}

export function useAckPulseAlert() {
  const auth = useAuth();
  return useApiMutation({
    live: (_fetch, { id }) =>
      pulseFetch(`/v1/alerts/${encodeURIComponent(id)}/ack`, {
        method: 'POST',
        headers: authHeader(auth.getAccessToken()),
      }),
    fixture: ({ id }) => ({ ok: true, id }),
    invalidate: ['pulse-alerts'],
    successMessage: (_, { id }) => `Alert ${id} bestätigt.`,
  });
}

export function usePulseSecurityEvents({ limit = 100, source = '' } = {}) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['pulse-events', { limit, source }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return eventsFixture(); }
      const qs = new URLSearchParams({ limit: String(limit) });
      if (source) qs.set('source', source);
      return pulseFetch(`/v1/security/events?${qs.toString()}`, { headers: authHeader(auth.getAccessToken()) });
    },
    refetchInterval: 20_000,
  });
}

export function usePulseDecisions({ activeOnly = true, limit = 100 } = {}) {
  const auth = useAuth();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['pulse-decisions', { activeOnly, limit }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return decisionsFixture(); }
      const qs = new URLSearchParams({ limit: String(limit), active: activeOnly ? '1' : '0' });
      return pulseFetch(`/v1/decisions?${qs.toString()}`, { headers: authHeader(auth.getAccessToken()) });
    },
    refetchInterval: 30_000,
  });
}
